import { useAccounts } from "@api/v1/hooks/useAccounts";
import { useMyself } from "@api/v1/hooks/useMyself";
import { Account } from "@api/v1/model";

/**
 * The bank code every Smartbroker+ account carries. Same value the bank select
 * whitelists as `BDWBDEMM003` / slug `SmartbrokerPlus`.
 */
const SMARTBROKER_PLUS_BANK_CODE = "BDWBDEMM003";

/**
 * The Smartbroker+ depot a user can still update: WAPI-12275's criteria for a
 * Smartbroker+ account, narrowed by two things the notice depends on.
 *
 * `01_depot`, because a Smartbroker+ settlement account passes the same bank
 * checks and `find` would take whichever comes first. The settlement account is
 * rendered as a depot's reference row, which carries no row id, so a notice
 * pointing at it would scroll nowhere.
 *
 * A bank connection, because updating one is the whole promise of the
 * `outdated` notice. A depot without it has no update action anywhere, so
 * sending that user to re-import is the honest advice, not a worse one.
 */
const isUpdatableSmartbrokerPlusDepot = (account: Account) =>
  account.type === "01_depot" &&
  account.bankConnectionType === "WEALTH_API" &&
  Boolean(account.externalId) &&
  Boolean(account.bankConnectionId) &&
  account.bankCode === SMARTBROKER_PLUS_BANK_CODE;

/**
 * `outdated` — the depot is still connected but has not synced for over 30
 * days, so the user updates it. `deleted` — the depot is gone, so the user
 * imports it again.
 */
export type SmartbrokerPlusExpiredVariant = "outdated" | "deleted";

type SmartbrokerPlusExpiredState = {
  isExpired: boolean;
  variant?: SmartbrokerPlusExpiredVariant;
  /**
   * The still-connected Smartbroker+ account, so the notice can link straight
   * at its row. Only set for the `outdated` variant — in the `deleted` case
   * there is no account left to point at.
   */
  accountId?: number;
};

/**
 * Whether to tell the user their Smartbroker+ premium access expired, and which
 * of the two notices applies.
 *
 * The backend reports expiry through a single role and does not say which of
 * the two conditions triggered it, so the variant is decided here: the 30-day
 * check already happened when the role was assigned, leaving only the question
 * of whether the depot still exists.
 *
 * Nothing is reported until both the roles and the accounts have loaded. A
 * failure on either leaves `isExpired` false, so a user never sees a notice
 * derived from incomplete data.
 */
export const useSmartbrokerPlusExpired = (): SmartbrokerPlusExpiredState => {
  const { data: myself, isFetched: isMyselfFetched } = useMyself();

  const hasExpiredRole = Boolean(myself?.isSmartbrokerPlusExpired);

  // Accounts are only needed to pick the variant, so they are not requested for
  // the users who will never see the notice.
  const {
    data: accounts,
    isFetched: areAccountsFetched,
    isError: didAccountsFail,
  } = useAccounts({}, { enabled: isMyselfFetched && hasExpiredRole });

  // `isFetched` is also true for a request that failed, which would leave
  // `accounts` undefined, match nothing, and tell the user to re-import a depot
  // they still have — starting a duplicate import. Both the error and the
  // missing list have to close the gate, not just the pending state.
  if (
    !isMyselfFetched ||
    !hasExpiredRole ||
    !areAccountsFetched ||
    didAccountsFail ||
    !accounts?.accounts
  ) {
    return { isExpired: false };
  }

  const smartbrokerPlusDepot = accounts.accounts.find(
    isUpdatableSmartbrokerPlusDepot
  );

  return smartbrokerPlusDepot
    ? {
        isExpired: true,
        variant: "outdated",
        accountId: smartbrokerPlusDepot.id,
      }
    : { isExpired: true, variant: "deleted" };
};
